import axios from 'axios';
import {
    MIContent,
    MIFilterState,
    MIPagedResponse,
    MITrendingResponse,
    StockIntelligence,
    StockReport,
    StockReportExists,
} from '@/models/market-intelligence';

const BASE = '/interface/api/market_intelligence';

export const getMIArticles = async (filters: MIFilterState = {}, signal?: AbortSignal): Promise<MIPagedResponse<MIContent>> => {
    const params: Record<string, any> = {};
    if (filters.contentType) params.content_type = filters.contentType;
    if (filters.theme) params.theme = filters.theme;
    if (filters.ticker) params.ticker = filters.ticker;
    if (filters.q) params.q = filters.q;
    if (filters.page) params.page = filters.page;
    params.page_size = filters.pageSize || 20;

    const res = await axios.get<MIPagedResponse<MIContent>>(`${BASE}/articles/`, { params, signal });
    return res.data;
};

export const getMIPulses = async (filters: MIFilterState = {}, signal?: AbortSignal): Promise<MIPagedResponse<MIContent>> => {
    const params: Record<string, any> = {};
    if (filters.pulseType) params.pulse_type = filters.pulseType;
    if (filters.severity) params.severity = filters.severity;
    if (filters.ticker) params.ticker = filters.ticker;
    if (filters.q) params.q = filters.q;
    if (filters.page) params.page = filters.page;
    params.page_size = filters.pageSize || 30;

    const res = await axios.get<MIPagedResponse<MIContent>>(`${BASE}/pulses/`, { params, signal });
    return res.data;
};

export const getMIContent = async (id: number, signal?: AbortSignal): Promise<MIContent> => {
    const res = await axios.get<MIContent>(`${BASE}/content/${id}/`, { signal });
    return res.data;
};

/** One content item for a link (?show=article|pulse&id=): rejects bad ids and error payloads (4xx resolve under validateStatus). */
export const loadMIContent = async (id: string, signal?: AbortSignal): Promise<MIContent> => {
    if (!/^\d+$/.test(id)) throw new Error("bad id");
    const content = await getMIContent(Number(id), signal);
    if (!content?.id || !content.headline) throw new Error("not content");
    return content;
};

export const getMITrending = async (signal?: AbortSignal): Promise<MITrendingResponse> => {
    const res = await axios.get<MITrendingResponse>(`${BASE}/trending/`, { params: { limit: 10 }, signal });
    return res.data;
};

export const getMIStock = async (ticker: string, signal?: AbortSignal): Promise<StockIntelligence> => {
    const res = await axios.get<StockIntelligence>(`${BASE}/stock/${ticker.toUpperCase()}/`, { signal });
    return res.data;
};

export const postNewsletterSubscribe = async (email: string): Promise<void> => {
    await axios.post(`${BASE}/newsletter/subscribe/`, { email });
};

// ───────────────────────── Stock Reports ─────────────────────────
// Premium per-company long-form research the MI pipeline publishes
// weekly. Backend endpoints:
//   GET /stock_reports/                  — paginated list
//   GET /stock_reports/<ticker>/         — full body by ticker
//   GET /stock_reports/<ticker>/exists/  — cheap probe used by the
//       stock-overview callout so we don't pull the body until the
//       user clicks through.

export interface StockReportListFilters {
    ticker?: string;
    page?: number;
    pageSize?: number;
}

export const getStockReports = async (
    filters: StockReportListFilters = {},
    signal?: AbortSignal,
): Promise<MIPagedResponse<StockReport>> => {
    const params: Record<string, any> = {};
    if (filters.ticker) params.ticker = filters.ticker;
    if (filters.page) params.page = filters.page;
    params.page_size = filters.pageSize || 20;

    const res = await axios.get<MIPagedResponse<StockReport>>(`${BASE}/stock_reports/`, { params, signal });
    return res.data;
};

export const getStockReport = async (
    ticker: string,
    signal?: AbortSignal,
    exchange?: string,
): Promise<StockReport> => {
    // ``?exchange=`` disambiguates cross-region tickers — e.g. BYND
    // is listed on both NYSE and LSE; without it the backend's
    // ``_resolve_ti_for_ticker`` picks the primary listing, which
    // may not match the page the caller is rendering.
    const params = exchange ? { exchange } : undefined;
    const res = await axios.get<StockReport>(
        `${BASE}/stock_reports/${ticker.toUpperCase()}/`,
        { signal, params },
    );
    return res.data;
};

export const getStockReportExists = async (
    ticker: string,
    signal?: AbortSignal,
    exchange?: string,
): Promise<StockReportExists> => {
    // Same cross-region disambiguation as ``getStockReport``.
    const params = exchange ? { exchange } : undefined;
    const res = await axios.get<StockReportExists>(
        `${BASE}/stock_reports/${ticker.toUpperCase()}/exists/`,
        { signal, params },
    );
    return res.data;
};

// ── Previous Stock Report versions ───────────────────────────────────
// Every regeneration of a Stock Report archives the prior row into
// ``StockReportRevision``. This endpoint surfaces those for the FE
// "view earlier versions" picker. Newest first; empty list is the
// expected response for a first-version company.

export interface StockReportRevision {
    id: number;
    version: number;
    headline: string;
    summary: string;
    body: string;
    bodyTruncated: boolean;
    locked: boolean;
    llmModel: string;
    promptVersion: string;
    relevanceScore: number | null;
    iterationsUsed: number | null;
    generatedAt: string;
    publishedAt: string | null;
}

export interface StockReportRevisionsResponse {
    ticker: string;
    length: number;
    revisions: StockReportRevision[];
}

export const getStockReportRevisions = async (
    ticker: string,
    signal?: AbortSignal,
    exchange?: string,
): Promise<StockReportRevisionsResponse> => {
    const params = exchange ? { exchange } : undefined;
    const res = await axios.get<StockReportRevisionsResponse>(
        `${BASE}/stock_reports/${ticker.toUpperCase()}/revisions/`,
        { signal, params },
    );
    return res.data;
};

// ───────────────────────── Market Commentary ─────────────────────────
// The seven cross-stock, market-wide articles previously served from
// /ask_ortex/latest_by_model/. They moved into MIContent under
// content_type=market_commentary with a theme discriminator. The
// per-page consumers (analyst note on /analysts, events note on
// /events, etc.) now hit getCommentaryLatest(theme); the new
// /market-intelligence Market Commentary tab uses getMIArticles({
// contentType: 'market_commentary' }) for the listing.

export type CommentaryTheme =
    | 'general'
    | 'short_interest'
    | 'analyst'
    | 'events'
    | 'insider'
    | 'options'
    | 'money_flow'
    | 'alt_data';

export const COMMENTARY_THEMES: { value: CommentaryTheme; label: string }[] = [
    { value: 'general', label: 'General' },
    { value: 'short_interest', label: 'Short Interest' },
    { value: 'analyst', label: 'Analyst' },
    { value: 'events', label: 'Events' },
    { value: 'insider', label: 'Insider' },
    { value: 'options', label: 'Options' },
    { value: 'money_flow', label: 'Money Flow' },
    { value: 'alt_data', label: 'Alt Data' },
];

// Single fetch function replacing the seven near-identical getXNote()
// controllers. Returns MIContent directly (full headline, body,
// provenance, publishedAt) so consumers can choose how much to render.
export const getCommentaryLatest = async (
    theme: CommentaryTheme,
    signal?: AbortSignal,
): Promise<MIContent> => {
    const res = await axios.get<MIContent>(
        `${BASE}/commentary/${theme}/latest/`,
        { signal },
    );
    return res.data;
};

// Legacy-shape wrapper for the seven existing per-page consumers
// (AINote etc.) that still expect {response, obfuscated}. Lets the
// per-page integrations stop reaching for /ask_ortex/ without
// rewriting every component that renders <AINote/>. The
// ``obfuscated`` field is always false today — premium gating on
// commentary is a separate, future concern.
export interface LegacyNoteResponse {
    response?: string;
    obfuscated?: boolean;
}

export const getCommentaryLatestAsLegacyNote = async (
    theme: CommentaryTheme,
    signal?: AbortSignal,
): Promise<LegacyNoteResponse> => {
    try {
        const row = await getCommentaryLatest(theme, signal);
        return {
            response: row.body || '',
            obfuscated: false,
        };
    } catch (err: any) {
        // 404 = no commentary yet for this theme. Return empty
        // response so the AINote renderer's "no data" path fires
        // without throwing.
        if (err?.response?.status === 404) {
            return { response: '', obfuscated: false };
        }
        throw err;
    }
};

// ───────────────────────── Notifications ─────────────────────────
// A user's MI subscriptions (what they want and on which channel) and
// their in-app inbox. Backed by /subscriptions/ and /inbox/ on the MI
// API; delivery runs server-side (market_intelligence/notifications.py
// in ted). The MI API renders camelCase and accepts it on input.

// push: the in-app inbox plus a push to the user's phones (ortex/ted/ted#2485).
export type MIChannel = 'in_app' | 'email' | 'push';

export interface MISubscription {
    id: number;
    tradingitem: number | null;
    ticker: string | null;
    contentType: string;
    pulseType: string;
    minSeverity: string;
    channel: MIChannel;
    active: boolean;
    createdAt: string;
    updatedAt: string;
}

export type MISubscriptionInput = Partial<
    Pick<MISubscription, 'contentType' | 'pulseType' | 'minSeverity' | 'channel' | 'active'>
> & { ticker?: string };

export interface MIInboxItem {
    id: number;
    sentAt: string | null;
    readAt: string | null;
    content: {
        id: number;
        contentType: string;
        pulseType: string;
        theme: string;
        ticker: string | null;
        headline: string;
        summary: string;
        severity: string;
        isPremium: boolean;
        publishedAt: string | null;
        url: string;
    };
}

export interface MIInbox {
    unread: number;
    results: MIInboxItem[];
}

export const getMISubscriptions = async (signal?: AbortSignal): Promise<MISubscription[]> => {
    const res = await axios.get<{ results: MISubscription[] }>(`${BASE}/subscriptions/`, { signal });
    return res.data.results;
};

export const createMISubscription = async (input: MISubscriptionInput): Promise<MISubscription> => {
    const res = await axios.post<MISubscription>(`${BASE}/subscriptions/`, input);
    return res.data;
};

export const updateMISubscription = async (id: number, input: MISubscriptionInput): Promise<MISubscription> => {
    const res = await axios.patch<MISubscription>(`${BASE}/subscriptions/${id}/`, input);
    return res.data;
};

export const deleteMISubscription = async (id: number): Promise<void> => {
    await axios.delete(`${BASE}/subscriptions/${id}/`);
};

export const getMIInbox = async (signal?: AbortSignal): Promise<MIInbox> => {
    const res = await axios.get<MIInbox>(`${BASE}/inbox/`, { signal });
    return res.data;
};

export const markMIInboxRead = async (ids?: number[]): Promise<number> => {
    const res = await axios.post<{ marked: number }>(`${BASE}/inbox/read/`, ids ? { ids } : { all: true });
    return res.data.marked;
};
